"use client";

import Image from "next/image";
import Link from "next/link";
import { motion, useReducedMotion } from "@/lib/motion";
import { ArrowLeft, Clock } from "@/components/icons";
import { HeroGlow } from "@/components/ui";
import CodeBlock from "@/components/ui/code-block";
import FaqSection from "@/components/sections/products/shared/faq";
import ProxyPricingToggle from "@/components/sections/shared/proxy-pricing-toggle";
import CtaSection from "@/components/sections/shared/cta";
import { getReadingTimeFromBlocks } from "@/lib/reading-time";
import { scrollToId } from "@/lib/utils";

function isPipeTable(text: string): boolean {
  const lines = text
    .trim()
    .split("\n")
    .filter((l) => l.trim());
  if (lines.length < 2) return false;
  return lines.filter((l) => l.includes("|")).length >= Math.ceil(lines.length * 0.8);
}

function parseRow(line: string): string[] {
  const parts = line.split("|").map((c) => c.trim());
  if (parts[0] === "") parts.shift();
  if (parts[parts.length - 1] === "") parts.pop();
  return parts;
}

const TABLE_INLINE_PATTERN = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g;

/** Preformatted table cells are raw pasted text, not Prismic rich-text spans, so
 *  `**bold**` and `[label](url)` markers never go through `applySpans` — parse
 *  them here instead. */
function renderInlineMarkdown(text: string, nofollowSet?: Set<string>): React.ReactNode {
  if (!text.includes("**") && !text.includes("](")) return text;
  const parts = text.split(TABLE_INLINE_PATTERN).filter(Boolean);
  let offset = 0;
  return parts.map((part) => {
    const start = offset;
    offset += part.length;

    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={start} className="text-foreground font-semibold">
          {part.slice(2, -2)}
        </strong>
      );
    }

    const linkMatch = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part);
    if (linkMatch) {
      const [, label, href] = linkMatch;
      const isExternal = !href.includes("proxidize.com") && !href.startsWith("/");
      const isNofollow = nofollowSet?.has(href);
      const relParts = [
        "noopener",
        ...(isExternal ? ["noreferrer"] : []),
        ...(isNofollow ? ["nofollow"] : []),
      ];
      return (
        <a
          key={start}
          href={href}
          className="text-foreground underline underline-offset-2 hover:opacity-80"
          target={isExternal ? "_blank" : undefined}
          rel={relParts.join(" ")}
        >
          {label}
        </a>
      );
    }

    return <span key={start}>{part}</span>;
  });
}

function ProseTable({ text, nofollowSet }: { text: string; nofollowSet?: Set<string> }) {
  const lines = text
    .trim()
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l && !/^[\s|:-]+$/.test(l));
  const [headerLine, ...dataLines] = lines;
  const headers = parseRow(headerLine);
  const rows = dataLines.map(parseRow);

  return (
    <div className="border-border my-6 overflow-x-auto rounded-xl border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-border bg-card border-b">
            {headers.map((h) => (
              <th
                key={h}
                className="text-foreground px-4 py-3 text-left text-xs font-semibold tracking-wide whitespace-nowrap uppercase"
              >
                {renderInlineMarkdown(h, nofollowSet)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="[&>tr:nth-child(even)]:bg-surface-2">
          {rows.map((row) => (
            <tr key={row.join("|")} className="border-border/50 border-b last:border-0">
              {row.map((cell) => (
                <td key={cell} className="text-muted-foreground px-4 py-3 leading-relaxed">
                  {renderInlineMarkdown(cell, nofollowSet)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

type RichTextSpan = { type: string; start: number; end: number; data?: { url?: string } };
type RichTextNode = { type: string; text: string; spans: RichTextSpan[]; direction?: string };
type ContentBlock = {
  body_content: RichTextNode[];
  image: { url?: string; alt?: string } | Record<string, never>;
  caption: string | null;
  nofollow_urls: string | null;
  code_language?: string | null;
};
type FAQ = { question: string | null; answer: RichTextNode[] };

function escapeHtml(str: string) {
  return (str ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function applySpans(text: string, spans: RichTextSpan[], nofollowSet?: Set<string>): string {
  const safeText = text ?? "";
  if (!spans?.length) return escapeHtml(safeText);
  const sorted = [...spans].sort(
    (a, b) => a.start - b.start || b.end - b.start - (a.end - a.start)
  );
  const processed = new Set<number>();
  let result = "";
  let cursor = 0;
  for (let i = 0; i < sorted.length; i++) {
    if (processed.has(i)) continue;
    const span = sorted[i];
    if (span.start < cursor) {
      processed.add(i);
      continue;
    }
    result += escapeHtml(safeText.slice(cursor, span.start));
    const nestedSpans: RichTextSpan[] = [];
    for (let j = i + 1; j < sorted.length; j++) {
      const s = sorted[j];
      if (s.start >= span.start && s.end <= span.end) {
        nestedSpans.push({ ...s, start: s.start - span.start, end: s.end - span.start });
        processed.add(j);
      }
    }
    const innerText = safeText.slice(span.start, span.end);
    const inner = nestedSpans.length
      ? applySpans(innerText, nestedSpans, nofollowSet)
      : escapeHtml(innerText);
    if (span.type === "strong") result += `<strong>${inner}</strong>`;
    else if (span.type === "em") result += `<em>${inner}</em>`;
    else if (span.type === "hyperlink" && span.data?.url) {
      const isExternal = !span.data.url.includes("proxidize.com") && !span.data.url.startsWith("/");
      const isNofollow = nofollowSet?.has(span.data.url);
      const relParts = [
        "noopener",
        ...(isExternal ? ["noreferrer"] : []),
        ...(isNofollow ? ["nofollow"] : []),
      ];
      const targetAttr = isExternal ? ` target="_blank"` : "";
      result += `<a href="${span.data.url}" class="text-foreground underline underline-offset-2 hover:opacity-80"${targetAttr} rel="${relParts.join(" ")}">${inner}</a>`;
    } else result += inner;
    cursor = span.end;
  }
  result += escapeHtml(safeText.slice(cursor));
  return result;
}

type RichGroup =
  | { type: "ul"; items: RichTextNode[] }
  | { type: "ol"; items: RichTextNode[] }
  | { type: "node"; node: RichTextNode };

function groupNodes(nodes: RichTextNode[]): RichGroup[] {
  const groups: RichGroup[] = [];
  for (const node of nodes) {
    const last = groups[groups.length - 1];
    if (node.type === "list-item") {
      if (last?.type === "ul") {
        last.items.push(node);
      } else {
        groups.push({ type: "ul", items: [node] });
      }
    } else if (node.type === "o-list-item") {
      if (last?.type === "ol") {
        last.items.push(node);
      } else {
        groups.push({ type: "ol", items: [node] });
      }
    } else {
      groups.push({ type: "node", node });
    }
  }
  return groups;
}

function RichText({
  nodes,
  nofollowSet,
  codeLang,
}: {
  nodes: RichTextNode[];
  nofollowSet?: Set<string>;
  codeLang?: string | null;
}) {
  if (!nodes?.length) return null;
  const groups = groupNodes(nodes);
  return (
    <div className="space-y-4">
      {groups.map((group) => {
        if (group.type === "ul") {
          return (
            <ul
              key={`ul-${group.items[0]?.text?.slice(0, 30) || "empty"}`}
              className="list-outside list-disc space-y-1 pl-5"
            >
              {group.items.map((node) => {
                const text = node.text ?? "";
                const html = applySpans(text, node.spans, nofollowSet);
                const dir = node.direction === "rtl" ? "rtl" : undefined;
                return (
                  <li
                    key={`${node.type}-${text.slice(0, 40)}`}
                    dir={dir}
                    className="text-muted-foreground 3xl:text-lg leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: html }}
                  />
                );
              })}
            </ul>
          );
        }
        if (group.type === "ol") {
          return (
            <ol
              key={`ol-${group.items[0]?.text?.slice(0, 30) || "empty"}`}
              className="list-outside list-decimal space-y-1 pl-5"
            >
              {group.items.map((node) => {
                const text = node.text ?? "";
                const html = applySpans(text, node.spans, nofollowSet);
                const dir = node.direction === "rtl" ? "rtl" : undefined;
                return (
                  <li
                    key={`${node.type}-${text.slice(0, 40)}`}
                    dir={dir}
                    className="text-muted-foreground 3xl:text-lg leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: html }}
                  />
                );
              })}
            </ol>
          );
        }
        const { node } = group;
        const text = node.text ?? "";
        const html = applySpans(text, node.spans, nofollowSet);
        const dir = node.direction === "rtl" ? "rtl" : undefined;
        const nodeKey = `${node.type}-${text.slice(0, 40)}`;
        switch (node.type) {
          case "heading1":
            return (
              <h2
                key={nodeKey}
                dir={dir}
                className="3xl:text-4xl text-foreground mt-10 mb-4 scroll-mt-24 text-3xl font-black"
                dangerouslySetInnerHTML={{ __html: html }}
              />
            );
          case "heading2":
            return (
              <h2
                key={nodeKey}
                dir={dir}
                className="3xl:text-3xl text-foreground mt-10 mb-4 scroll-mt-24 text-2xl font-bold"
                dangerouslySetInnerHTML={{ __html: html }}
              />
            );
          case "heading3":
            return (
              <h3
                key={nodeKey}
                dir={dir}
                className="text-foreground mt-8 mb-3 scroll-mt-24 text-lg font-semibold"
                dangerouslySetInnerHTML={{ __html: html }}
              />
            );
          case "heading4":
            return (
              <h4
                key={nodeKey}
                dir={dir}
                className="3xl:text-xl text-foreground mt-6 mb-2 text-lg font-semibold"
                dangerouslySetInnerHTML={{ __html: html }}
              />
            );
          case "heading5":
          case "heading6":
            return (
              <h5
                key={nodeKey}
                dir={dir}
                className="3xl:text-lg text-foreground mt-6 mb-2 text-base font-semibold"
                dangerouslySetInnerHTML={{ __html: html }}
              />
            );
          case "preformatted":
            if (isPipeTable(text))
              return <ProseTable key={nodeKey} text={text} nofollowSet={nofollowSet} />;
            return <CodeBlock key={nodeKey} code={text} lang={codeLang ?? undefined} />;
          default:
            return text ? (
              <p
                key={nodeKey}
                dir={dir}
                className="text-muted-foreground 3xl:text-lg leading-relaxed"
                dangerouslySetInnerHTML={{ __html: html }}
              />
            ) : null;
        }
      })}
    </div>
  );
}

interface Props {
  title: string;
  description: string | null;
  date: string;
  contentBlocks: ContentBlock[];
  faqs: FAQ[];
}

export function PrismicProxyArticle({ title, description, date, contentBlocks, faqs }: Props) {
  const prefersReducedMotion = useReducedMotion();
  const readTime = getReadingTimeFromBlocks(contentBlocks);

  return (
    <main id="main-content" tabIndex={-1} className="relative isolate overflow-y-clip">
      {/* The page-hero ribbon. These article templates have no hero section, so
          HeroGlow bounds itself to a band at the top of the document and lands
          where it does on every other page. The main must NOT clip the X axis —
          the ribbon is 100vw and has to bleed past the shell's max-w-7xl frame. */}
      <HeroGlow align="left" behind />
      <section className="3xl:max-w-5xl relative mx-auto max-w-4xl px-4 pt-28 pb-8 sm:px-6 lg:px-8">
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <Link
            href="/proxy-server"
            className="text-muted-foreground hover:text-foreground mb-8 inline-flex items-center gap-1.5 text-sm transition-colors"
          >
            <ArrowLeft size={14} />
            Back to Proxy Server
          </Link>

          <div className="mb-5 flex flex-wrap items-center gap-3">
            <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
              <Clock size={11} aria-hidden="true" />
              {readTime}
            </span>
            <span className="text-muted-foreground text-xs">{date}</span>
          </div>

          <h1 className="text-foreground mb-4 text-center text-3xl leading-tight font-black tracking-tight sm:text-4xl lg:text-5xl">
            {title}
          </h1>

          {description && (
            <p className="text-muted-foreground mx-auto mt-4 max-w-2xl text-center text-lg leading-relaxed">
              {description}
            </p>
          )}

          <div className="mt-8 flex justify-center">
            <span className="cta-glow-btn-wrap">
              <button
                type="button"
                onClick={() => scrollToId("proxy-pricing")}
                className="cta-glow-btn-inner"
              >
                See Pricing
              </button>
            </span>
          </div>
        </motion.div>
      </section>

      <motion.article
        initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        className="3xl:max-w-5xl mx-auto max-w-4xl px-4 pb-24 sm:px-6 lg:px-8"
      >
        <div className="space-y-8">
          {contentBlocks.map((block, i) => {
            const hasText = block.body_content?.some((n) => n.text?.trim());
            const imgUrl = (block.image as { url?: string })?.url;
            const nofollowSet = block.nofollow_urls
              ? new Set(
                  block.nofollow_urls
                    .split("\n")
                    .map((u) => u.trim())
                    .filter(Boolean)
                )
              : undefined;
            const blockKey =
              block.body_content?.[0]?.text?.slice(0, 30) ??
              (imgUrl ? `img-${i}` : `block-empty-${i}`);

            return (
              <div key={blockKey}>
                {hasText && (
                  <RichText
                    nodes={block.body_content}
                    nofollowSet={nofollowSet}
                    codeLang={block.code_language}
                  />
                )}
                {imgUrl && (
                  <figure className="my-6">
                    <div className="border-foreground/6 overflow-hidden rounded-xl border">
                      <Image
                        src={imgUrl}
                        alt={(block.image as { alt?: string }).alt ?? block.caption ?? ""}
                        width={1200}
                        height={630}
                        className="h-auto w-full object-cover"
                      />
                    </div>
                    {block.caption && (
                      <figcaption className="text-muted-foreground mt-2 text-center text-xs">
                        {block.caption}
                      </figcaption>
                    )}
                  </figure>
                )}
              </div>
            );
          })}
        </div>
      </motion.article>

      {faqs.length > 0 && (
        <FaqSection
          faqs={faqs.map((faq) => ({
            q: faq.question ?? "",
            a: <RichText nodes={faq.answer} />,
          }))}
          description="Quick answers to the most common questions about this topic."
          hideContactLink
        />
      )}

      <ProxyPricingToggle />
      <CtaSection />
    </main>
  );
}
